import {useState} from 'react'
import {useCredentialModal} from '../../contexts'
import {ReserveSlotResponse, TimeSlot} from '../../types/store/basket'
import {CredentialModalPlacementMap} from '../../types/ui'
import {validateShippingAddress} from '../../utils/address-utils'
import {useBasketStore, useCustomerStore, usePreBookStore} from './useStore'
import {useDisclosure} from '../../vanilla'
import {useSlotExpiration} from './useSlotExpiration'
import useDeliveryBooking from '../../hooks/use-delivery-booking'

export const useSlotReservation = (isPrebook = false) => {
  const {
    currentDeliverySlotId,
    isAppliedPostCodeDeliverable,
    shippingAddress
  } = useBasketStore()
  const {currentPreBookDeliverySlotId, preBookAddress} = usePreBookStore()
  const slotAddress = isPrebook ? preBookAddress : shippingAddress
  const slotAddressId = isPrebook ? preBookAddress?.addressId : shippingAddress?.c_customerAddressID
  const {isReservationExpired} = useSlotExpiration()
  const {isRegistered, addresses} = useCustomerStore()
  const {reserveSlot} = useDeliveryBooking()
  const {showCredentialModal} = useCredentialModal()
  const {preBookDeliveryAddress} = usePreBookStore()

  const confirmationModal = useDisclosure(false)
  const changeSlotWarningModal = useDisclosure(false)
  const errorBookingSlotModal = useDisclosure(false)

  const [bookSlotErrorCode, setBookSlotErrorCode] = useState('success')
  const currentBookedSlot = isPrebook ? currentPreBookDeliverySlotId : currentDeliverySlotId
  const customerHasBookedSlot = Boolean(currentBookedSlot)

  const handleShippingAddressValidation = () => {
    if (!slotAddress) return
    const shippingAddressValidation = validateShippingAddress(slotAddress)
    return shippingAddressValidation.isValid
  }

  const handleSlotReservation = async (slot: TimeSlot) => {
    const customerAddressSelected = addresses.find(
      (address) => address.addressId === slotAddressId,
    )

    if (!customerAddressSelected) {
      window.scrollTo({top: 0})
      return false
    }

    if (isRegistered) {
      if (customerHasBookedSlot 
      && isAppliedPostCodeDeliverable
      && !isReservationExpired()
      && !changeSlotWarningModal.isOpen) {
        changeSlotWarningModal.onOpen()
        return
      }

      if (!handleShippingAddressValidation()) {
        setBookSlotErrorCode('300')
        errorBookingSlotModal.onOpen()
        return
      }

      const slotReservation: ReserveSlotResponse = await reserveSlot(slot.id, false)
      const reservationStatus = slotReservation.result?.code

      if (reservationStatus && reservationStatus === 'success') {
        confirmationModal.onOpen()
      } else {
        if (reservationStatus) {
          setBookSlotErrorCode(reservationStatus)
        }
        errorBookingSlotModal.onOpen()
      }
      return
    }
  }

  /**
   * Reserves a Pre-book slot for the given slot and postcode
   * @param {TimeSlot} slot the slot to reserve
   * @returns false on failure, otherwise void
   */
  const handlePreBookSlotReservation = async (slot: TimeSlot) => {
    if(!preBookDeliveryAddress?.postalCode) {
      console.error('Pre-book delivery address postal code is not set')
      return false
    }

    if (isRegistered) {
      const slotReservation: ReserveSlotResponse = await reserveSlot(slot.id, true)
      const reservationStatus = slotReservation.result?.code

      if (reservationStatus && reservationStatus === 'success') {
        
        confirmationModal.onOpen()
      } else {
        if (reservationStatus) {
          setBookSlotErrorCode(reservationStatus)
        }
        errorBookingSlotModal.onOpen()
      }
    }
  }

  const handleSlotRebook = async (slotId: string, showConfirmationModal: boolean) => {
    const type =
      window.innerWidth < 768
        ? CredentialModalPlacementMap.BOTTOM
        : CredentialModalPlacementMap.ATTACHED

    if (type === CredentialModalPlacementMap.ATTACHED) {
      window.scrollTo({top: 0})
    }

    showCredentialModal(type, async () => {
      if (customerHasBookedSlot && slotId !== currentBookedSlot) {
        changeSlotWarningModal.onOpen()
      } else {
        try {
          await reserveSlot(slotId, isPrebook)
          if (showConfirmationModal) {
            confirmationModal.onOpen()
          }
        } catch (error) {
          errorBookingSlotModal.onOpen()
        }
      }
    })
  }

  return {
    bookSlotErrorCode,
    changeSlotWarningModal,
    confirmationModal,
    customerHasBookedSlot,
    errorBookingSlotModal,
    handlePreBookSlotReservation,
    handleShippingAddressValidation,
    handleSlotRebook,
    handleSlotReservation,
  }
}
